import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Button, Dropdown } from "react-bootstrap";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import "bootstrap/dist/css/bootstrap.min.css";
import { Toast, Spinner } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';

ChartJS.register(ArcElement, Tooltip, Legend);

const testURL = process.env.REACT_APP_TEST_URL;
const productionURL = process.env.REACT_APP_PRODUCTION_URL;

const Sidebar = ({ setFilter, fetchData }) => {
    const handleClick = (filterType) => {
        fetchData(); // Fetch latest data
        setFilter(filterType); // Set filter after fetching
    };
    return (
        <div className="sidebar">
            <h4 className="sidebar-title">RSF 2.0</h4>
            <a href="#" className="sidebar-link" onClick={() => { fetchData(); setFilter("ALL"); }}>
                Dashboard
            </a>
            <a href="#" className="sidebar-link" onClick={() => { fetchData(); setFilter("SETTLED"); }}>
                Settled Transactions
            </a>
            <a href="#" className="sidebar-link" onClick={() => { fetchData(); setFilter("UNSETTLED"); }}>
                Unsettled Transactions
            </a>
            <a href="#" className="sidebar-link">Disputed Settlements</a>

            {/* Sidebar Custom Styles */}
            <style jsx>{`
          .sidebar {
            width: 220px;
            height: 100vh;
            background: linear-gradient(135deg, #2c3e50, #34495e);
            color: white;
            padding: 20px;
            box-shadow: 3px 0px 10px rgba(0, 0, 0, 0.2);
            display: flex;
            flex-direction: column;
          }
          .sidebar-title {
            font-size: 1.5rem;
            font-weight: bold;
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid rgba(255, 255, 255, 0.3);
            padding-bottom: 10px;
          }
          .sidebar-link {
            color: white;
            text-decoration: none;
            display: block;
            font-size: 1rem;
            padding: 12px 15px;
            border-radius: 8px;
            transition: background 0.3s ease, transform 0.2s ease;
            cursor: pointer;
          }
          .sidebar-link:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: scale(1.05);
          }
             .summary-box {
    min-width: 220px;
    text-align: center;
    transition: transform 0.2s ease-in-out;
  }
  .summary-box:hover {
    transform: scale(1.05);
  }
        `}</style>
        </div>
    );
};


const Header = ({ totalTransactions, totalSettled, totalUnsettled }) => {
    return (
        <div className="top-section p-4 bg-white shadow-sm mb-3 rounded">
            {/* Title */}
            <h1 className="mb-4 text-center" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem", fontWeight: "600", color: "#333" }}>
                Settlement and Reconciliation Report
            </h1>

            {/* Boxes */}
            <div className="row g-3">
                <div className="col-md-4">
                    <div className="info-box bg-primary text-white p-4 rounded d-flex align-items-center justify-content-between">
                        <div>
                            <h5 className="mb-1">📊 Total Transactions</h5>
                            <h3>{totalTransactions}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="info-box bg-success text-white p-4 rounded d-flex align-items-center justify-content-between">
                        <div>
                            <h5 className="mb-1">✔ Settled Transactions</h5>
                            <h3>{totalSettled}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="info-box bg-danger text-white p-4 rounded d-flex align-items-center justify-content-between">
                        <div>
                            <h5 className="mb-1">❌ Unsettled Transactions</h5>
                            <h3>{totalUnsettled}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Optional custom styles */}
            <style jsx>{`
              .info-box h5 {
                  font-weight: 500;
                  font-size: 1rem;
              }
              .info-box h3 {
                  font-size: 1.8rem;
                  margin-top: 5px;
                  font-weight: bold;
              }
                 

          `}</style>
        </div>
    );
};


const TransactionTable = ({ transactions, showActions, onReport, onRecon }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // success | error | warning | info
    const [reconStatusData, setReconStatusData] = useState(null);
    const [showReconStatusModal, setShowReconStatusModal] = useState(false);
    const [reconData, setReconData] = useState(null);
    const [showNoModal, setShowNoModal] = useState(false);
    const [showResettlementModal, setShowResettlementModal] = useState(false);
    const [resettlementData, setResettlementData] = useState([]);




    // Function to fetch transaction details
    const fetchTransactionDetails = async (transactionId) => {
        setLoading(true);
        setSelectedTransaction(null); // Clear previous data

        try {
            const response = await fetch(productionURL + "/api/ondc/getSettlement", {
                method: "POST", // Must be POST
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(transactionId) // Send transactionId as raw JSON
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch transaction details: ${response.status}`);
            }

            const data = await response.json();
            console.log("API Response:", data); // Debugging log
            setSelectedTransaction(data);
        } catch (error) {
            console.error("Error fetching details:", error);
            setSelectedTransaction(null);
        }

        setLoading(false);
        setShowModal(true);
    };
    const handleSelfSettle = async (transactionId) => {


        try {
            const response = await axios.post(productionURL + '/api/ondc/settleMISC', `"${transactionId}"`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200) {
                setSnackbarMessage('Self settle transaction has been initiated!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage('Something went wrong during self settlement.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error(error);
            setSnackbarMessage('An error occurred while initiating self settlement.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };
    const handleReconStatus = async (settlementId) => {
        try {
            const response = await axios.get(productionURL + `/api/ondc/getReconDetails/${settlementId}`);
            setReconStatusData(response.data);
            setShowReconStatusModal(true);
        } catch (error) {
            console.error("Error fetching recon status:", error);
            setSnackbarMessage("Failed to fetch recon status.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleAgreeApi = async (data) => {
        try {
            const response = await axios.post(
                productionURL + "/api/ondc/agree_on_recon",
                data,
                {
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.status === 200) {
                setSnackbarMessage("Reconciliation confirmation sent successfully.");
                setSnackbarSeverity("success");
            } else {
                setSnackbarMessage("Failed to confirm reconciliation.");
                setSnackbarSeverity("error");
            }
        } catch (error) {
            console.error("API Error:", error);
            setSnackbarMessage("Error sending reconciliation confirmation.");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
        }
    };
    const handleRejectRecon = async () => {
        try {
            const response = await axios.post(
                productionURL + "/api/ondc/disagree_on_recon",
                reconStatusData,
                { headers: { "Content-Type": "application/json" } }
            );

            if (response.status === 200) {
                setSnackbarMessage("Rejection submitted successfully");
                setSnackbarSeverity("success");
                setShowNoModal(false);
            } else {
                setSnackbarMessage("Submission failed");
                setSnackbarSeverity("error");
            }
        } catch (error) {
            console.error("Submission error:", error);
            setSnackbarMessage("Error occurred");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
        }
    };
    const handleResettlementCheck = async () => {
        try {
            const response = await axios.get(productionURL + "/api/ondc/getReSettlementDetails");

            setResettlementData(response.data || []);
            setShowResettlementModal(true);
        } catch (error) {
            console.error("Error fetching resettlement details:", error);
            alert("Failed to fetch resettlement details.");
        }
    };
    const handleDoSettle = async (settlementId) => {
        try {
            const response = await axios.post(productionURL + '/api/ondc/reSettlement', `"${settlementId}"`, {
                headers: { 'Content-Type': 'application/json' }
            });
    
            // Show success alert (you can replace this with a custom Snackbar)
            alert('call for re Settlement completed successfully!');
        } catch (error) {
            console.error(error);
            alert('Failed to complete settlement!');
        }
    };
    
    return (
        <div className="info-section bg-white shadow-sm p-3 rounded">
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Table striped bordered hover className="custom-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Settlement ID</th>
                        <th>Transaction ID</th>
                        <th>Order ID</th>
                        <th>NP Settlement Status</th>
                        <th>Self Settlement Status</th>
                        <th>Order Amount ₹</th>
                        <th>Settlement Amount ₹</th>
                        <th>Collector Amount ₹</th>
                        <th>Store Name</th>
                        <th>Order Date</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.length > 0 ? (
                        transactions.map((txn, index) => (
                            <tr
                                key={txn.id}
                                onClick={(e) => {
                                    if (!e.target.closest(".dropdown")) {
                                        fetchTransactionDetails(txn.transanctionId);
                                    }
                                }}
                                style={{ cursor: "pointer" }}
                            >
                                <td>{index + 1}</td>
                                <td>{txn.settlementId}</td>
                                <td>{txn.transanctionId}</td>
                                <td>{txn.orderId}</td>
                                <td className={txn.interparticipantSettlementStatus === "SETTLED" ? "text-success" : "text-danger"}>
                                    {txn.interparticipantSettlementStatus}
                                </td>
                                <td>{txn.collectorSettlementStatus || "N/A"}</td>
                                <td>₹{txn.orderAmount || "N/A"}</td>
                                <td>₹{txn.settlementAmount || "N/A"}</td>
                                <td>₹{txn.collectorAmount || "N/A"}</td>
                                <td>{txn.storeName}</td>
                                <td>{txn.orderDate}</td>



                                <td>
                                    {txn.interparticipantSettlementStatus === "SETTLED" ? (
                                        <select className="form-select form-select-sm w-auto"
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                if (e.target.value === 'self') {
                                                    handleSelfSettle(txn.settlementId);
                                                }
                                            }}> // Prevent row click
                                            <option value="self">Self Settled</option>
                                            <option value="check">Check Status</option>
                                        </select>
                                    ) : (
                                        <Dropdown className="dropdown">
                                            <Dropdown.Toggle variant="secondary" size="sm">
                                                Action
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => onReport(txn.transanctionId)}>Call For Settlement Status</Dropdown.Item>
                                                <Dropdown.Item onClick={() => onRecon(txn.transanctionId)}>Reconciliation call from Buyer</Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleReconStatus(txn.settlementId)}>Reconciliation Call Status from Seller</Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleResettlementCheck()}>Check for Resettlement details</Dropdown.Item>

                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </td>

                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={showActions ? "11" : "10"} className="text-center text-muted">No Transactions Found</td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Transaction Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Settlement Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" />
                            <p>Loading transaction details...</p>
                        </div>
                    ) : selectedTransaction ? (
                        <table className="table table-bordered custom-table">
                            <tbody>
                                <tr><td><strong>Transaction ID:</strong></td><td>{selectedTransaction.transanctionId}</td></tr>
                                <tr><td><strong>Settlement ID:</strong></td><td>{selectedTransaction.settlementId}</td></tr>
                                <tr><td><strong>Order ID:</strong></td><td>{selectedTransaction.orderId || "N/A"}</td></tr>
                                <tr><td><strong>NP Settlement Status:</strong></td><td>{selectedTransaction.interparticipantSettlementStatus}</td></tr>
                                <tr><td><strong>Self Settlement Status:</strong></td><td>{selectedTransaction.collectorSettlementStatus}</td></tr>
                                <tr><td><strong>Order Amount:</strong></td><td>₹{selectedTransaction.orderAmount ?? 0.0}</td></tr>
                                <tr><td><strong>Settlement Amount:</strong></td><td>₹{selectedTransaction.settlementAmount ?? 0.0}</td></tr>
                                <tr><td><strong>Collector Amount:</strong></td><td>₹{selectedTransaction.collectorAmount ?? 0.0}</td></tr>
                                <tr><td><strong>WithHolding Amount:</strong></td><td>₹{selectedTransaction.withHoldingAmount ?? 0.0}</td></tr>
                                <tr><td><strong>TCS Amount:</strong></td><td>₹{selectedTransaction.tcsAmount ?? 0.0}</td></tr>
                                <tr><td><strong>TDS Amount:</strong></td><td>₹{selectedTransaction.tdsAmount ?? 0.0}</td></tr>
                                <tr><td><strong>Store Name:</strong></td><td>{selectedTransaction.storeName}</td></tr>
                                <tr><td><strong>Order Date:</strong></td><td>{selectedTransaction.orderDate}</td></tr>
                                <tr><td><strong>BagId:</strong></td><td>{selectedTransaction.bagId}</td></tr>
                                <tr><td><strong>NP Settlement ReferenceNo:</strong></td><td>{selectedTransaction.interparticipantSettlementReferenceNo}</td></tr>
                                <tr><td><strong>Collector Settlement ReferenceNo:</strong></td><td>{selectedTransaction.collectorSettlementReferenceNo}</td></tr>
                                <tr><td><strong>Error Code:</strong></td><td>{selectedTransaction.errorCode}</td></tr>
                                <tr><td><strong>Error Message:</strong></td><td>{selectedTransaction.errorMessage}</td></tr>
                                <tr><td><strong>NPSettlement ErrorCode:</strong></td><td>{selectedTransaction.npSettlementErrorCode}</td></tr>
                                <tr><td><strong>NPSettlement ErrorMessage:</strong></td><td>{selectedTransaction.npSettlementErrorMessage}</td></tr>
                                <tr><td><strong>SelfSettlement ErrorCode:</strong></td><td>{selectedTransaction.selfSettlementErrorCode}</td></tr>
                                <tr><td><strong>SelfSettlement ErrorMessage:</strong></td><td>{selectedTransaction.selfSettlementErrorMessage}</td></tr>
                                <tr><td><strong>Recon Accord:</strong></td><td>{selectedTransaction.reconAccord}</td></tr>
                                {/* Difference Amount Fields with Color Highlighting */}
                                {["settlementDiffAmount", "withHoldingDiffAmount", "tcsDiffAmount", "tdsDiffAmount", "collectorDiffAmount"].map((key) => {
                                    const value = selectedTransaction[key] || "N/A";
                                    return (
                                        <tr key={key}>
                                            <td><strong>{key.replace(/([A-Z])/g, " $1").trim()}:</strong></td>
                                            <td
                                                style={{
                                                    backgroundColor:
                                                        value == null
                                                            ? "gray"
                                                            : parseFloat(value) === 0
                                                                ? "green"
                                                                : "red",
                                                    color: value == null ? "black" : "white"
                                                }}
                                            >
                                                {value == null ? "N/A" : `₹${value}`}
                                            </td>
                                        </tr>


                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <p>No Data Available</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showReconStatusModal} onHide={() => setShowReconStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Recon Call Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {reconStatusData ? (
                        <div className="recon-details">
                            <p><strong>Settlement ID:</strong> {reconStatusData.settlementId}</p>
                            <p><strong>Transaction ID:</strong> {reconStatusData.transactionId}</p>
                            <p><strong>Order Id:</strong> ₹{reconStatusData.orderId}</p>
                            <p><strong>Order Amount:</strong> ₹{reconStatusData.orderAmount}</p>
                            <p><strong>Settlement Amount:</strong> ₹{reconStatusData.settlementAmount}</p>
                            <p><strong>Settlement Status:</strong> {reconStatusData.settlementStatus}</p>
                            <p><strong>Commission Amount:</strong> ₹{reconStatusData.commissionAmount}</p>
                            <p><strong>WithHolding Amount:</strong> ₹{reconStatusData.withHoldingAmount}</p>
                            <p><strong>TCS Amount:</strong> ₹{reconStatusData.tcsAmount ?? 0.0}</p>
                            <p><strong>TDS Amount:</strong> ₹{reconStatusData.tdsAmount ?? 0.0}</p>
                            <p><strong>Updated At:</strong> {reconStatusData.updatedAt}</p>
                            <p><strong>BPP ID:</strong> {reconStatusData.bpP_Id}</p>
                            <p><strong>BPP URL:</strong> {reconStatusData.bpP_Url}</p>
                            <p>
                                <strong>Reconciliation Agree:</strong>{" "}
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleAgreeApi(reconStatusData)}
                                    className="me-2"
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                        setReconData(reconStatusData);
                                        setShowNoModal(true);
                                    }}
                                >
                                    No
                                </Button>
                            </p>



                        </div>
                    ) : (
                        <p>No data available.</p>
                    )}
                </Modal.Body>
            </Modal>
            <Modal show={showNoModal} onHide={() => setShowNoModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Reconciliation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {reconData && (
                        <div className="recon-details">
                            <p><strong>Settlement ID:</strong> {reconData.settlementId}</p>
                            <p><strong>Transaction ID:</strong> {reconData.transactionId}</p>
                            <p><strong>Order Id:</strong> ₹{reconData.orderId}</p>
                            <p><strong>Order Amount:</strong> ₹{reconData.orderAmount}</p>
                            <p><strong>Settlement Amount:</strong> ₹{reconData.settlementAmount}</p>
                            <p><strong>Status:</strong> {reconData.settlementStatus}</p>
                            <p><strong>Commission Amount:</strong> ₹{reconStatusData.commissionAmount}</p>
                            <p><strong>WithHolding Amount:</strong> ₹{reconStatusData.withHoldingAmount}</p>
                            <p><strong>TCS Amount:</strong> ₹{reconStatusData.tcsAmount ?? 0.0}</p>
                            <p><strong>TDS Amount:</strong> ₹{reconStatusData.tdsAmount ?? 0.0}</p>
                            <p><strong>Updated At:</strong> {reconStatusData.updatedAt}</p>
                            <p><strong>BPP ID:</strong> {reconStatusData.bpP_Id}</p>
                            <p><strong>BPP URL:</strong> {reconStatusData.bpP_Url}</p>
                            <div className="container">
                                <div className="row mb-2">
                                    <div className="col-6">
                                        <label><strong>Settlement Diff Amount</strong></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={reconStatusData.settlementDiffValue || ''}
                                            onChange={(e) =>
                                                setReconStatusData({
                                                    ...reconStatusData,
                                                    settlementDiffValue: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label><strong>Withholding Diff Amount</strong></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={reconStatusData.withHoldingDiffValue || ''}
                                            onChange={(e) =>
                                                setReconStatusData({
                                                    ...reconStatusData,
                                                    withHoldingDiffValue: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="row mb-2">
                                    <div className="col-6">
                                        <label><strong>TCS Diff Amount</strong></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={reconStatusData.tcsDiffValue || ''}
                                            onChange={(e) =>
                                                setReconStatusData({
                                                    ...reconStatusData,
                                                    tcsDiffValue: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label><strong>TDS Diff Amount</strong></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={reconStatusData.tdsDiffValue || ''}
                                            onChange={(e) =>
                                                setReconStatusData({
                                                    ...reconStatusData,
                                                    tdsDiffValue: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="row mb-2">
                                    <div className="col-6">
                                        <label><strong>Collector Diff Amount</strong></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={reconStatusData.commissionDiffValue || ''}
                                            onChange={(e) =>
                                                setReconStatusData({
                                                    ...reconStatusData,
                                                    commissionDiffValue: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>


                            {/* Add any fields you want to show */}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowNoModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            handleRejectRecon(reconData);
                            setShowNoModal(false);
                        }}
                    >
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showResettlementModal}
                onHide={() => setShowResettlementModal(false)}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Resettlement Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="table-responsive">
                        <Table striped bordered hover className="mb-0 resettlement-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Settlement ID</th>
                                    <th>Order ID</th>
                                    <th>Order Amount ₹</th>
                                    <th>Settlement Amount ₹</th>
                                    <th>Collector Amount ₹</th>
                                    <th>WithHolding ₹</th>
                                    <th>TCS ₹</th>
                                    <th>TDS ₹</th>
                                    <th>Due Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resettlementData.map((txn, index) => (
                                    <tr key={txn.id}>
                                        <td>{index + 1}</td>
                                        <td>{txn.settlementId}</td>
                                        <td>{txn.orderId}</td>
                                        <td>₹{txn.orderAmount}</td>
                                        <td>₹{txn.settlementAmount}</td>
                                        <td>₹{txn.collectorAmount}</td>
                                        <td>₹{txn.withHoldingAmount}</td>
                                        <td>₹{txn.tcsAmount}</td>
                                        <td>₹{txn.tdsAmount}</td>
                                        <td>{txn.dueDate}</td>
                                        <td>
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleDoSettle(txn.settlementId)}
                                            >
                                                Do Settle
                                            </Button>

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>



            {/* Custom Styles */}
            <style jsx>{`
          .custom-table th {
            font-size: 0.85rem;
            font-weight: 600;
            background-color: #f8f9fa;
            color: #333;
            text-align: center;
            padding: 12px;
          }
          .custom-table td {
            font-size: 0.8rem;
            padding: 10px;
            text-align: center;
          }
          .custom-table th, .custom-table td {
            border: 1px solid #dee2e6;
          }
          .custom-table tr:hover {
            background-color: #f9f9f9;
          }
            .recon-details {
  font-family: 'Arial', sans-serif;
  font-size: 13px;
  line-height: 1.6;
}
.recon-details p {
  margin-bottom: 6px;
}



.resettlement-table td,
.resettlement-table th {
  font-size: 0.75rem; /* Increase font size */
  padding: 0.50rem; /* More spacing */
}

.resettlement-table {
  font-size: 1rem;
}

        `}</style>
        </div>
    );
};

const Dashboard = () => {
    // 🟢 State Hooks (inside component)
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [totalSettled, setTotalSettled] = useState(0);
    const [totalUnsettled, setTotalUnsettled] = useState(0);
    const [filter, setFilter] = useState("ALL");

    // 🟢 State for API calls and toasts
    const [loading, setLoading] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [showToast, setShowToast] = useState(false);



    // 🛠 Fetch Data Function
    const fetchData = () => {
        fetch(productionURL + "/api/ondc/getSettlementDetails")
            .then((response) => response.json())
            .then((data) => {
                setTransactions(data.ondcSettlementOrders || []);
                setTotalTransactions(data.totalNoTransaction || 0);
                setTotalSettled(data.totalNoOfSettled || 0);
                setTotalUnsettled(data.totalNoOfUnsettled || 0);
            })
            .catch((error) => console.error("Error fetching data:", error));
    };

    // 🛠 Handle API Call for Report/Recon
    const handleApiCall = async (url, transactionId, actionType) => {
        setLoading(true);  // Show loading overlay
        setShowToast(false);

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(transactionId),
            });

            const data = await response.json();

            if (data && data.transanctionId) {
                setTransactions((prevTransactions) =>
                    prevTransactions.map((txn) =>
                        txn.transanctionId === transactionId
                            ? {
                                ...txn,
                                interparticipantSettlementStatus: data.interparticipantSettlementStatus,
                                collectorSettlementStatus: data.collectorSettlementStatus,
                            }
                            : txn
                    )
                );
                setToastMessage("✅ Transaction status updated successfully!");
            } else {
                setToastMessage("⚠️ Update received, but no changes made.");
            }
        } catch (error) {
            console.error(`Error in ${actionType}:`, error);
            setToastMessage("❌ Request failed! Please try again.");
        }

        setLoading(false);  // Hide loading overlay
        setShowToast(true);
    };



    const handleReportClick = (transactionId) => {
        handleApiCall(productionURL + "/api/ondc/report", transactionId, "Report");
    };

    const handleReconClick = (transactionId) => {
        handleApiCall(productionURL + "/api/ondc/recon", transactionId, "Reconciliation");
    };

    // 🔄 Apply Filtering when transactions or filter changes
    useEffect(() => {
        let filteredData = transactions;
        if (filter === "SETTLED") {
            filteredData = transactions.filter((txn) => txn.interparticipantSettlementStatus === "SETTLED");
        } else if (filter === "UNSETTLED") {
            filteredData = transactions.filter((txn) => txn.interparticipantSettlementStatus !== "SETTLED");
        }
        setFilteredTransactions(filteredData);
    }, [transactions, filter]);

    useEffect(() => {
        if (filter === "ALL") {
            setFilteredTransactions(transactions);
        } else {
            const filtered = transactions.filter(
                (txn) => txn.interparticipantSettlementStatus === filter
            );
            setFilteredTransactions(filtered);
        }
    }, [filter, transactions]);

    // 🔄 Fetch Data on Initial Mount
    useEffect(() => {
        fetchData();
    }, []);

    return (
        <div className="d-flex" style={{ height: "100vh", overflow: "hidden" }}>
            <Sidebar setFilter={setFilter} fetchData={fetchData} />
            <div className="flex-grow-1 p-3" style={{ overflowY: "auto" }}>
                {loading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" role="status" />
                        <p>Loading data...</p>
                    </div>
                ) : (
                    <>
                        <Header totalTransactions={totalTransactions} totalSettled={totalSettled} totalUnsettled={totalUnsettled} />
                        <TransactionTable transactions={filteredTransactions} showActions={filter === "UNSETTLED"} onReport={handleReportClick} onRecon={handleReconClick} />
                    </>
                )}
                {/* ✅ Toast Notification */}
                {showToast && (
                    <Toast
                        style={{
                            position: "fixed",
                            bottom: "20px",
                            right: "20px",
                            backgroundColor: "#343a40",
                            color: "white",
                            padding: "10px 15px",
                            borderRadius: "8px",
                            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                        }}
                        onClose={() => setShowToast(false)}
                        delay={3000}
                        autohide
                    >
                        <Toast.Body>{toastMessage}</Toast.Body>
                    </Toast>
                )}

                {loading && (
                    <div className="loading-overlay">
                        <div className="spinner-container">
                            <Spinner animation="border" variant="light" />
                            <p>Processing your request...</p>
                        </div>
                    </div>
                )}

                <style jsx>{`
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
  .spinner-container {
    text-align: center;
    color: white;
    font-size: 1.2rem;
    font-weight: bold;
  }
`}</style>


            </div>
        </div>

    );

};


export default Dashboard;